import "features/messages/src/styles/ChannelSearch/index.scss";
import { useEffect, useState } from "react";

import { Dropdown, Input } from "antd";
import { useChatContext } from "stream-chat-react";
import { debounce as _debounce } from "common/utils";

import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";

import UserBadge from "../UserBadge/UserBadge";
import { channelByUser } from "./utils";

const ResultLabel = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.4;
  min-width: 0;
`;

const ResultPrimary = styled.span`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultMeta = styled.span`
  color: rgba(0, 0, 0, 0.65);
  font-size: 11px;
`;

const SearchIcon = styled(SearchOutlined)`
  color: var(--sidebar-text-muted);
`;

const HashIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--primary-color);
  color: #fff;
  border-radius: 4px;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

const DropdownOverrides = styled.div`
  .ant-dropdown-menu {
    max-height: 320px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .ant-dropdown-menu-item {
    min-width: 0;
  }

  .ant-dropdown-menu-title-content {
    min-width: 0;
    overflow: hidden;
  }
`;

function SearchResultLabel({ primary, meta }) {
  return (
    <ResultLabel>
      <ResultPrimary>{primary}</ResultPrimary>
      {meta && <ResultMeta>{meta}</ResultMeta>}
    </ResultLabel>
  );
}

const renderDropdownOverlay = (menu) => <DropdownOverrides>{menu}</DropdownOverrides>;

export default function ChannelSearch() {
  const { client, setActiveChannel } = useChatContext();

  const [teamChannels, setTeamChannels] = useState([]);
  const [directChannels, setDirectChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!query) {
      setTeamChannels([]);
      setDirectChannels([]);
      setIsOpen(false);
    }
  }, [query]);

  const setChannel = (channel) => {
    setActiveChannel(channel);
    setQuery("");
    setIsOpen(false);
  };

  const selectUser = (user) => {
    channelByUser({ client, setActiveChannel, user });
    setQuery("");
    setIsOpen(false);
  };

  const getChannels = async (text) => {
    try {
      const channelResponse = client.queryChannels(
        { type: "team", name: { $autocomplete: text } },
        {},
        { limit: 5 },
      );
      const userResponse = client.queryUsers(
        { id: { $ne: client.userID || "" }, $and: [{ name: { $autocomplete: text } }] },
        { id: 1 },
        { limit: 5 },
      );
      const [channels, { users }] = await Promise.all([channelResponse, userResponse]);
      setTeamChannels(channels);
      setDirectChannels(users);
    } catch {
      setTeamChannels([]);
      setDirectChannels([]);
    }
    setLoading(false);
  };

  const getChannelsDebounce = _debounce(getChannels, 100, { trailing: true });

  const onSearch = (event) => {
    const { value } = event.target;
    setQuery(value);
    if (value) {
      setLoading(true);
      setIsOpen(true);
      getChannelsDebounce(value);
    }
  };

  const buildChannelChildren = () => {
    if (loading && teamChannels.length === 0) {
      return [{ key: "channels-loading", label: <i>Loading...</i>, disabled: true }];
    }
    if (teamChannels.length === 0) {
      return [{ key: "channels-empty", label: <i>No channels found</i>, disabled: true }];
    }
    return teamChannels.map((ch) => ({
      key: `channel-${ch.id}`,
      icon: <HashIcon>#</HashIcon>,
      label: (
        <SearchResultLabel
          primary={ch.data?.name || ch.id}
          meta={ch.data?.id ? `Channel ID: ${ch.data.id}` : null}
        />
      ),
      onClick: () => setChannel(ch),
    }));
  };

  const buildUserChildren = () => {
    if (loading && directChannels.length === 0) {
      return [{ key: "users-loading", label: <i>Loading...</i>, disabled: true }];
    }
    if (directChannels.length === 0) {
      return [{ key: "users-empty", label: <i>No direct messages found</i>, disabled: true }];
    }
    return directChannels.map((u) => ({
      key: `user-${u.id}`,
      icon: <UserBadge name={u.name || u.id} userId={u.id} image={u.image} size={24} />,
      label: <SearchResultLabel primary={u.name || u.id} meta={u.email || `User ID: ${u.id}`} />,
      onClick: () => selectUser(u),
    }));
  };

  const items = query
    ? [
        {
          type: "group",
          key: "channels-group",
          label: "Channels",
          children: buildChannelChildren(),
        },
        {
          type: "group",
          key: "users-group",
          label: "Users",
          children: buildUserChildren(),
        },
      ]
    : [];

  return (
    <div className="channel-search__container">
      <Dropdown
        menu={{ items }}
        open={isOpen && items.length > 0}
        onOpenChange={(open) => setIsOpen(open && !!query)}
        trigger={["click"]}
        dropdownRender={renderDropdownOverlay}
        getPopupContainer={(trigger) => trigger.parentElement || document.body}
      >
        <Input
          prefix={<SearchIcon />}
          placeholder="Search here..."
          value={query}
          onChange={onSearch}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
          }}
          allowClear
        />
      </Dropdown>
    </div>
  );
}
